import { useEffect, useState } from "react";
import { Lock, LogOut, Inbox, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { LEAD_STATUSES, StatusPicker } from "@/components/LeadStatus";
import { LeadNotes } from "@/components/LeadNotes";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("vector_admin_token") || "");
  const [pin, setPin] = useState("");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [filter, setFilter] = useState("all");
  const [updating, setUpdating] = useState("");

  const updateStatus = async (id, status) => {
    setUpdating(id);
    const prev = leads;
    setLeads((cur) => ({ ...cur, leads: cur.leads.map((l) => (l.id === id ? { ...l, status } : l)) }));
    try {
      await api.patch(`/admin/leads/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Marked as ${status}`);
    } catch (e) {
      setLeads(prev);
      toast.error("Could not update status");
      if (e.response?.status === 401) logout();
    } finally {
      setUpdating("");
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      await api.patch(`/admin/leads/${id}/notes`, { notes }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((cur) => ({ ...cur, leads: cur.leads.map((l) => (l.id === id ? { ...l, notes } : l)) }));
      toast.success("Note saved");
    } catch (e) {
      toast.error("Could not save note");
      if (e.response?.status === 401) logout();
      throw e;
    }
  };

  const load = (t) => {
    setChecking(true);
    api
      .get("/admin/leads", { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => setLeads(r.data))
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem("vector_admin_token");
          setToken("");
        }
        setError("Could not load enquiries — sign in again.");
      })
      .finally(() => setChecking(false));
  };

  useEffect(() => {
    if (token) load(token);
  }, []); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setChecking(true);
    try {
      const r = await api.post("/admin/login", { pin });
      localStorage.setItem("vector_admin_token", r.data.token);
      setToken(r.data.token);
      load(r.data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || "Login failed — try again.");
    } finally {
      setChecking(false);
      setPin("");
    }
  };

  const logout = () => {
    localStorage.removeItem("vector_admin_token");
    setToken("");
    setLeads(null);
    setError("");
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/leads/export", { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vector-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Could not export leads");
      if (e.response?.status === 401) logout();
    }
  };

  const field =
    "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-slate-600 focus:border-neon/60 focus:outline-none focus:ring-1 focus:ring-neon/40 transition-colors tracking-[0.3em]";

  return (
    <>
      <SEO title="Owner Login — Vector Growth and Advisory" description="Owner access: captured enquiries." />
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden px-6 pt-28 pb-16" data-testid="admin-page">
        <div className="spot pointer-events-none absolute inset-0" />

        {!token && (
          <Reveal className="relative w-full max-w-md">
            <div className="rounded-2xl border hairline bg-surface/70 p-8 md:p-10" data-testid="admin-login-card">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-neon/30 bg-neon/5">
                <Lock className="h-5 w-5 text-neon" />
              </div>
              <Eyebrow className="mt-6">Owner access</Eyebrow>
              <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight">Leads inbox</h1>
              <p className="mt-2 text-sm text-slate-500">Enter your PIN to review captured enquiries.</p>
              <form onSubmit={login} className="mt-6 space-y-4">
                <input
                  type="password"
                  inputMode="numeric"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="••••••"
                  className={field}
                  data-testid="admin-pin-input"
                  aria-label="Admin PIN"
                />
                {error && <p className="text-xs text-red-600" data-testid="admin-error">{error}</p>}
                <button
                  type="submit"
                  disabled={checking || pin.length < 4}
                  data-testid="admin-login-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-neon px-6 py-3 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-all duration-300 hover:shadow-[0_0_35px_rgba(47,168,224,0.35)] disabled:opacity-50"
                >
                  {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock inbox"}
                </button>
              </form>
            </div>
          </Reveal>
        )}

        {token && (
          <div className="relative w-full max-w-[1200px]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <Eyebrow>Owner access</Eyebrow>
                <h1 className="mt-3 flex items-center gap-3 font-display text-3xl font-extrabold tracking-tight">
                  <Inbox className="h-7 w-7 text-neon" /> Leads inbox
                  <span className="rounded-full border border-neon/40 bg-neon/10 px-3 py-1 font-mono text-xs text-neon" data-testid="admin-leads-count">
                    {leads ? leads.count : "…"}
                  </span>
                </h1>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={exportCsv}
                  disabled={!leads || leads.count === 0}
                  data-testid="admin-export-button"
                  className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600 transition-colors hover:border-neon/60 hover:text-neon disabled:opacity-40"
                >
                  <Download className="h-3.5 w-3.5" /> Export CSV
                </button>
                <button
                  onClick={logout}
                  data-testid="admin-logout-button"
                  className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600 transition-colors hover:border-red-400/60 hover:text-red-300"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            </div>

            {error && <p className="mt-6 text-xs text-red-600" data-testid="admin-error">{error}</p>}

            {leads && leads.leads.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-status-filter">
                {[{ id: "all", label: "All" }, ...LEAD_STATUSES].map((s) => {
                  const n = s.id === "all" ? leads.leads.length : leads.leads.filter((l) => l.status === s.id).length;
                  const active = filter === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setFilter(s.id)}
                      data-testid={`admin-filter-${s.id}`}
                      aria-pressed={active}
                      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                        active ? "border-ink bg-ink text-white" : "hairline bg-white text-slate-500 hover:text-ink"
                      }`}
                    >
                      {s.label}
                      <span className={`rounded-full px-1.5 text-[9px] ${active ? "bg-white/20" : "bg-elevated"}`}>{n}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-6 overflow-x-auto rounded-2xl border hairline bg-surface/60" data-testid="admin-leads-table">
              {leads === null && (
                <div className="p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Loading enquiries…</div>
              )}
              {leads && leads.leads.length === 0 && (
                <div className="p-10 text-center text-sm text-slate-500" data-testid="admin-leads-empty">
                  No enquiries yet — they will appear here the moment the contact form is used.
                </div>
              )}
              {leads && leads.leads.length > 0 && (
                <table className="w-full min-w-[1280px] text-left text-sm">
                  <thead>
                    <tr className="border-b hairline font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4">Received</th>
                      <th className="px-5 py-4">Name</th>
                      <th className="px-5 py-4">Email</th>
                      <th className="px-5 py-4">Company</th>
                      <th className="px-5 py-4">Interest</th>
                      <th className="px-5 py-4">Message</th>
                      <th className="px-5 py-4">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.leads.filter((l) => filter === "all" || l.status === filter).length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-10 text-center text-sm text-slate-500" data-testid="admin-filter-empty">
                          No enquiries with this status.
                        </td>
                      </tr>
                    )}
                    {leads.leads.filter((l) => filter === "all" || l.status === filter).map((l) => (
                      <tr key={l.id} className="border-b border-slate-200 last:border-0 align-top transition-colors hover:bg-elevated/40" data-testid="admin-lead-row" data-status={l.status}>
                        <td className="px-5 py-4">
                          <StatusPicker
                            value={l.status}
                            disabled={updating === l.id}
                            onChange={(s) => updateStatus(l.id, s)}
                            testid={`lead-status-${l.id}`}
                          />
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 font-mono text-xs text-slate-500">
                          {new Date(l.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-5 py-4 font-medium text-slate-700">{l.name}</td>
                        <td className="px-5 py-4">
                          <a href={`mailto:${l.email}`} className="text-neon/90 hover:text-neon">{l.email}</a>
                        </td>
                        <td className="px-5 py-4 text-slate-600">{l.company || "—"}</td>
                        <td className="px-5 py-4 text-slate-600">{l.service}</td>
                        <td className="max-w-[320px] px-5 py-4 text-slate-500">{l.message}</td>
                        <td className="px-5 py-4">
                          <LeadNotes lead={l} onSave={saveNotes} testid={`lead-notes-${l.id}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
